import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { customerData } from '../../../utilities/CustomerData'
import { apiURL } from '../../../utilities/URLs'
import { refreshAccessToken } from '../../../utilities/extensions'

function getNextChargeStatus(nextChargeDate) {
  const normalizedDate = nextChargeDate.replace(/\//g, '-')
  const nextDate = new Date(normalizedDate + 'T00:00:00')
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  const diffDays = Math.ceil((nextDate - today) / (1000 * 60 * 60 * 24))
  const isOverdue = diffDays < 0
  const isUpcoming = diffDays >= 0 && diffDays <= 7

  let badgeText
  if (isOverdue)
    badgeText = `${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''} overdue`
  else if (isUpcoming)
    badgeText = `Due in ${diffDays} day${diffDays !== 1 ? 's' : ''}`
  else
    badgeText = `${diffDays} days away`

  return { nextDate, isOverdue, isUpcoming, badgeText, diffDays }
}

function formatInstallmentDate(dateStr) {
  const normalizedDate = dateStr.replace(/\//g, '-')
  const date = new Date(normalizedDate + 'T00:00:00')
  return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
}

function formatMoney(amount) {
  return Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2 })
}

function statusBadgeClass(status) {
  if (status === 'failed')
    return 'bg-red-100 text-red-700 border border-red-200'
  if (status === 'overdue')
    return 'bg-amber-100 text-amber-800 border border-amber-200'
  return 'bg-slate-100 text-slate-700 border border-slate-200'
}

function statusLabel(status) {
  if (status === 'failed')
    return 'Failed'
  if (status === 'overdue')
    return 'Overdue'
  return 'Upcoming'
}

async function fetchWithAuth(url, options = {}) {
  let res = await fetch(url, options)
  let data = await res.json()

  if (data.response?.error === 'Failed To Authenticate Token.') {
    const refreshedToken = await refreshAccessToken()
    if (refreshedToken && refreshedToken.length > 0) {
      res = await fetch(url, options)
      data = await res.json()
    } else {
      return { expired: true, data }
    }
  }

  return { expired: false, data }
}

export default function DashboardPaymentInstallments() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [installmentSummary, setInstallmentSummary] = useState(null)
  const [unpaidInstallments, setUnpaidInstallments] = useState([])
  const [lastFailedDebit, setLastFailedDebit] = useState(null)
  const [latestFailReason, setLatestFailReason] = useState(null)
  const [hasCardOnFile, setHasCardOnFile] = useState(false)
  const [canPayNextInstallment, setCanPayNextInstallment] = useState(false)
  const [payLoading, setPayLoading] = useState(false)
  const [payFeedback, setPayFeedback] = useState(null)
  const [showPaymentSelector, setShowPaymentSelector] = useState(false)
  const [showPayConfirm, setShowPayConfirm] = useState(false)
  const [selectedPaymentIds, setSelectedPaymentIds] = useState([])

  const leadId = customerData.customerLeadData.leadId

  const fetchStatus = async () => {
    setLoading(true)
    setError('')
    setPayFeedback(null)

    try {
      const { expired, data } = await fetchWithAuth(
        `${apiURL}/api/paymentInstallmentStatus?leadId=${encodeURIComponent(leadId)}`,
        { headers: { Authorization: customerData.accessToken } }
      )

      if (expired) {
        navigate('/', { state: { expired: true } })
        return
      }

      if (data.response?.error) {
        setError(data.response.error)
      } else {
        setInstallmentSummary(data.response.installmentSummary)
        setUnpaidInstallments(data.response.unpaidInstallments ?? [])
        setLastFailedDebit(data.response.lastFailedDebit)
        setLatestFailReason(data.response.latestFailReason)
        setHasCardOnFile(!!data.response.hasCardOnFile)
        setCanPayNextInstallment(!!data.response.canPayNextInstallment)
        setSelectedPaymentIds([])
        setShowPaymentSelector(false)
        setShowPayConfirm(false)
      }
    } catch {
      setError('Unable to load payment plan details. Please try again later.')
    }

    setLoading(false)
  }

  const selectedTotal = useMemo(() => {
    const selectedSet = new Set(selectedPaymentIds)
    return unpaidInstallments
      .filter((row) => selectedSet.has(row.paymentId))
      .reduce((sum, row) => sum + Number(row.amount), 0)
  }, [selectedPaymentIds, unpaidInstallments])

  const toggleInstallmentSelection = (index) => {
    const row = unpaidInstallments[index]
    if (!row)
      return

    const isSelected = selectedPaymentIds.includes(row.paymentId)
    if (isSelected) {
      setSelectedPaymentIds(unpaidInstallments.slice(0, index).map((item) => item.paymentId))
      return
    }

    setSelectedPaymentIds(unpaidInstallments.slice(0, index + 1).map((item) => item.paymentId))
  }

  const selectAllUnpaid = () => {
    setSelectedPaymentIds(unpaidInstallments.map((row) => row.paymentId))
  }

  const handlePayInstallments = async () => {
    if (selectedPaymentIds.length === 0)
      return

    setPayLoading(true)
    setPayFeedback(null)
    setShowPayConfirm(false)

    try {
      const form = new FormData()
      form.append('paymentIds', JSON.stringify(selectedPaymentIds))

      const { expired, data } = await fetchWithAuth(
        `${apiURL}/api/payPaymentInstallment?leadId=${encodeURIComponent(leadId)}`,
        {
          method: 'POST',
          headers: { Authorization: customerData.accessToken },
          body: form
        }
      )

      if (expired) {
        navigate('/', { state: { expired: true } })
        return
      }

      const result = data.response
      if (result?.ok) {
        const count = result.paymentCount ?? selectedPaymentIds.length
        setPayFeedback({
          type: 'success',
          text: `Payment of $${formatMoney(result.amount)} for ${count} installment${count !== 1 ? 's' : ''} processed successfully.`
        })
        await fetchStatus()
      } else {
        setPayFeedback({
          type: 'error',
          text: result?.message || result?.error || 'Payment could not be processed. Please try again or update your card.'
        })
      }
    } catch {
      setPayFeedback({ type: 'error', text: 'Something went wrong. Please try again.' })
    }

    setPayLoading(false)
  }

  useEffect(() => {
    fetchStatus()
  }, [])

  if (loading) {
    return (
      <div className='shadow-md p-6 rounded-xl w-full h-fit flex flex-col items-center bg-white font-mont gap-4 mt-4'>
        <p className='text-gray-500'>Loading your payment plan…</p>
      </div>
    )
  }

  if (error) {
    return (
      <div className='shadow-md p-6 rounded-xl w-full h-fit flex flex-col items-center bg-white font-mont gap-4 mt-4'>
        <p className='text-red-600 text-center'>{error}</p>
      </div>
    )
  }

  if (!installmentSummary)
    return null

  const remainingInstallments = installmentSummary.totalInstallments - installmentSummary.paidInstallments
  const progressPercent = installmentSummary.totalInstallments > 0
    ? Math.round((installmentSummary.paidInstallments / installmentSummary.totalInstallments) * 100)
    : 0

  let nextChargeStatus = null
  if (installmentSummary.nextChargeDate)
    nextChargeStatus = getNextChargeStatus(installmentSummary.nextChargeDate)

  const hasUnpaidInstallment = unpaidInstallments.length > 0
  const payAmount = installmentSummary.nextChargeAmount
    ? Number(installmentSummary.nextChargeAmount)
    : null
  const selectedCount = selectedPaymentIds.length

  return (
    <div className='w-full flex flex-col items-center gap-4 mt-4'>
      <div className='shadow-md p-6 rounded-xl w-full h-fit flex flex-col bg-white font-mont gap-6'>
        <div>
          <p className='font-bold text-2xl text-[#646464]'>Payment Installments</p>
          <p className='text-sm text-gray-500 mt-1'>
            Plan started {new Date(installmentSummary.planCreatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
          </p>
        </div>

        <div className='grid grid-cols-1 sm:grid-cols-3 gap-4'>
          <div className='p-4 rounded-lg bg-slate-50 border border-slate-200'>
            <p className='text-xs uppercase tracking-wider font-semibold text-gray-500'>Payments Made</p>
            <p className='text-2xl font-bold text-green-600 mt-1'>{installmentSummary.paidInstallments}</p>
          </div>
          <div className='p-4 rounded-lg bg-slate-50 border border-slate-200'>
            <p className='text-xs uppercase tracking-wider font-semibold text-gray-500'>Payments Remaining</p>
            <p className='text-2xl font-bold text-[#646464] mt-1'>{remainingInstallments}</p>
          </div>
          <div className='p-4 rounded-lg bg-slate-50 border border-slate-200'>
            <p className='text-xs uppercase tracking-wider font-semibold text-gray-500'>Total Payments</p>
            <p className='text-2xl font-bold text-[#646464] mt-1'>{installmentSummary.totalInstallments}</p>
          </div>
        </div>

        <div>
          <div className='flex items-center justify-between mb-2'>
            <p className='text-sm font-semibold text-gray-600'>Progress</p>
            <p className='text-sm font-semibold text-gray-600'>
              {installmentSummary.paidInstallments}/{installmentSummary.totalInstallments} paid
            </p>
          </div>
          <div className='w-full h-3 bg-slate-200 rounded-full overflow-hidden'>
            <div
              className='h-full bg-primaryColor rounded-full transition-all duration-300'
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>

        {hasUnpaidInstallment && nextChargeStatus ? (
          <div className={`p-4 rounded-xl border ${nextChargeStatus.isOverdue ? 'bg-red-50 border-red-200' : 'bg-slate-50 border-slate-200'}`}>
            <p className='text-xs uppercase tracking-wider font-semibold text-gray-500'>Next Scheduled Payment</p>
            <p className={`text-lg font-bold mt-1 ${nextChargeStatus.isOverdue ? 'text-red-600' : nextChargeStatus.isUpcoming ? 'text-amber-600' : 'text-green-600'}`}>
              {nextChargeStatus.nextDate.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
            </p>
            {payAmount !== null && (
              <p className='text-sm text-gray-600 mt-1'>
                Amount: <span className='font-semibold'>${formatMoney(payAmount)}</span>
              </p>
            )}
            <span className={`inline-block mt-2 px-2.5 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${
              nextChargeStatus.isOverdue
                ? 'bg-red-100 text-red-700 border border-red-200'
                : nextChargeStatus.isUpcoming
                  ? 'bg-amber-100 text-amber-700 border border-amber-200'
                  : 'bg-green-100 text-green-700 border border-green-200'
            }`}>
              {nextChargeStatus.badgeText}
            </span>
          </div>
        ) : (
          <div className='p-4 rounded-xl bg-green-50 border border-green-200'>
            <p className='font-semibold text-green-700'>All installments paid</p>
          </div>
        )}

        {(lastFailedDebit || latestFailReason) && (
          <div className='p-4 rounded-xl bg-red-50 border border-red-200'>
            <p className='text-xs uppercase tracking-wider font-semibold text-red-700'>Last Failed Payment</p>
            {lastFailedDebit && (
              <p className='text-sm text-gray-700 mt-2'>
                ${formatMoney(lastFailedDebit.debitPaymentAmount)}
                {' '}on{' '}
                {new Date(lastFailedDebit.debitPaymentCreatedAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
            )}
            {latestFailReason?.reason && (
              <p className='text-sm font-medium text-red-700 mt-1'>
                Reason: {latestFailReason.reason}
              </p>
            )}
            <p className='text-xs text-gray-500 mt-2'>
              Update your card on file, then use Make Payment Now below.
            </p>
          </div>
        )}

        {hasUnpaidInstallment && (
          <div className='p-4 rounded-xl border border-green-200 bg-green-50 flex flex-col gap-3'>
            <div>
              <p className='font-semibold text-[#646464]'>Make Payment Now</p>
              <p className='text-sm text-gray-600 mt-1'>
                Select one or more remaining installments and pay them together in a single charge to your card on file.
              </p>
            </div>

            {!hasCardOnFile ? (
              <div className='flex flex-col gap-2'>
                <p className='text-sm text-amber-700 font-medium'>Add or update your card on file before paying.</p>
                <button
                  type='button'
                  onClick={() => navigate('/update-card-on-file')}
                  className='px-6 py-3 bg-primaryColor text-white font-semibold rounded-lg shadow hover:bg-primaryDarkColor transition w-full sm:w-auto'
                >
                  Update card on file
                </button>
              </div>
            ) : showPaymentSelector ? (
              <div className='flex flex-col gap-3'>
                <div className='flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2'>
                  <p className='text-sm font-semibold text-gray-700'>Select payments to include</p>
                  <button
                    type='button'
                    onClick={selectAllUnpaid}
                    className='text-sm font-semibold text-primaryColor hover:underline self-start'
                  >
                    Select all remaining ({unpaidInstallments.length})
                  </button>
                </div>

                <p className='text-xs text-gray-500'>
                  Payments must be selected in plan order, starting with the earliest unpaid installment.
                </p>

                <ul className='max-h-72 overflow-y-auto rounded-lg border border-slate-200 bg-white divide-y divide-slate-100'>
                  {unpaidInstallments.map((row, index) => {
                    const isChecked = selectedPaymentIds.includes(row.paymentId)
                    const priorSelected = index === 0 || selectedPaymentIds.includes(unpaidInstallments[index - 1].paymentId)
                    const isDisabled = !priorSelected && !isChecked

                    return (
                      <li key={row.paymentId}>
                        <label
                          className={`flex items-start gap-3 p-3 cursor-pointer ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-slate-50'}`}
                        >
                          <input
                            type='checkbox'
                            className='mt-1 h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500'
                            checked={isChecked}
                            disabled={isDisabled || payLoading}
                            onChange={() => toggleInstallmentSelection(index)}
                          />
                          <span className='flex-1 min-w-0'>
                            <span className='flex flex-wrap items-center gap-2'>
                              <span className='font-semibold text-[#646464]'>
                                {formatInstallmentDate(row.date)}
                              </span>
                              <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-bold uppercase tracking-wider ${statusBadgeClass(row.status)}`}>
                                {statusLabel(row.status)}
                              </span>
                            </span>
                            <span className='block text-sm text-gray-600 mt-0.5'>
                              ${formatMoney(row.amount)}
                            </span>
                          </span>
                        </label>
                      </li>
                    )
                  })}
                </ul>

                {selectedCount > 0 && (
                  <p className='text-sm text-gray-700'>
                    Selected: <span className='font-bold'>{selectedCount}</span> payment{selectedCount !== 1 ? 's' : ''}{' '}
                    · Total: <span className='font-bold text-green-700'>${formatMoney(selectedTotal)}</span>
                  </p>
                )}

                {showPayConfirm ? (
                  <div className='flex flex-col gap-3 border-t border-green-200 pt-3'>
                    <p className='text-sm text-gray-700'>
                      Confirm a single charge of{' '}
                      <span className='font-bold'>${formatMoney(selectedTotal)}</span>
                      {' '}for {selectedCount} installment{selectedCount !== 1 ? 's' : ''} to your card on file?
                    </p>
                    <div className='flex flex-col sm:flex-row gap-2'>
                      <button
                        type='button'
                        disabled={payLoading || selectedCount === 0}
                        onClick={handlePayInstallments}
                        className='px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed'
                      >
                        {payLoading ? 'Processing…' : 'Confirm payment'}
                      </button>
                      <button
                        type='button'
                        disabled={payLoading}
                        onClick={() => setShowPayConfirm(false)}
                        className='px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-slate-50 transition disabled:opacity-50'
                      >
                        Back
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className='flex flex-col sm:flex-row gap-2'>
                    <button
                      type='button'
                      disabled={!canPayNextInstallment || payLoading || selectedCount === 0}
                      onClick={() => setShowPayConfirm(true)}
                      className='px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto'
                    >
                      {payLoading ? 'Processing…' : `Continue · $${formatMoney(selectedTotal)}`}
                    </button>
                    <button
                      type='button'
                      disabled={payLoading}
                      onClick={() => {
                        setShowPaymentSelector(false)
                        setSelectedPaymentIds([])
                        setShowPayConfirm(false)
                      }}
                      className='px-6 py-3 bg-white border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-slate-50 transition disabled:opacity-50 w-full sm:w-auto'
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button
                type='button'
                disabled={!canPayNextInstallment || payLoading}
                onClick={() => {
                  setShowPaymentSelector(true)
                  if (unpaidInstallments[0])
                    setSelectedPaymentIds([unpaidInstallments[0].paymentId])
                }}
                className='px-6 py-3 bg-green-600 text-white font-semibold rounded-lg shadow hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed w-full sm:w-auto'
              >
                Pay Now
              </button>
            )}

            {payFeedback && (
              <p
                role='status'
                className={`text-sm font-medium ${payFeedback.type === 'success' ? 'text-green-700' : 'text-red-600'}`}
              >
                {payFeedback.text}
              </p>
            )}
          </div>
        )}
      </div>

      <div className='shadow-md p-6 rounded-xl w-full h-fit flex flex-col items-center bg-white font-mont gap-4'>
        <p className='font-bold text-2xl text-center'>Update card on file</p>
        <p className='text-lg text-center text-gray-700'>
          Replace the card we keep on file for your account. No payment is processed — this only updates your saved card for future charges.
        </p>
        <button
          type='button'
          onClick={() => navigate('/update-card-on-file')}
          className='px-6 py-3 bg-white border-2 border-primaryColor text-primaryColor font-semibold rounded-lg shadow hover:bg-slate-50 transition'
        >
          Update card
        </button>
      </div>
    </div>
  )
}
